/**
 * Your calendars, read IN — the Time screen's "From your calendar" card.
 *
 * (calendarFeed.ts is the opposite direction: Project Hub's deadlines published
 * OUT as a feed for a calendar app to subscribe to.)
 *
 * Ported from the standalone time tracker, where it earned its place: meetings
 * are the time you forget to log, and turning each into one click is most of
 * the value. The behaviour is the tracker's, rule for rule — a short look-back
 * window, short events dropped, all-day/free/declined/cancelled left out by the
 * Worker, and anything already covered by a logged entry hidden.
 *
 * WHERE THE SETTINGS LIVE. A calendar's private address is a credential for the
 * whole calendar, and the list has to follow you from the laptop to the phone,
 * so the durable copy is a JSON `calendar` field on your own `users` record —
 * readable and writable only by you. localStorage is a cache in front of it,
 * and a fallback: a PocketBase that has not had the field added silently drops
 * it on write, and without the cache that would erase what you just typed.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { appMode, pb } from "./pocketbase";
import { toIsoLocal } from "./timeRollup";
import type { Project, TimeEntry } from "./types";

// The card exists to catch time you forgot to log today — its usefulness drops
// off a cliff after a couple of days. So the window is fixed and short: today
// plus the two days before it.
export const CALENDAR_LOOKBACK_DAYS = 3;
// Short blocks — a five-minute check-in, a travel buffer — are most of the noise.
export const DEFAULT_MIN_EVENT_MINUTES = 15;
export const MIN_EVENT_CHOICES = [0, 10, 15, 30] as const;
// A working surface, not an inbox. Past a handful of rows it stops being a shortcut.
export const MAX_CALENDAR_SUGGESTIONS = 5;
// Matches the Worker's ceiling (worker/calendarImport.ts).
export const MAX_CALENDARS = 5;
// The Worker caches each feed for five minutes; refetching faster only spends
// round trips to be told the same thing.
const STALE_MS = 5 * 60 * 1000;

export interface CalendarConnection {
  label: string;
  icalUrl: string;
  /** Off = not read anywhere. */
  enabled: boolean;
  /** Offer this calendar's meetings as Time entries. A personal calendar can
   *  be on for the Today grid and off here. Absent = on, as before. */
  suggest?: boolean;
  /** Its colour on the Today grid, from CALENDAR_PALETTE. Absent = by position. */
  color?: string;
}

export interface CalendarSettings {
  connections: CalendarConnection[];
  minEventMinutes: number;
  /** Occurrence ids already dismissed. Kept with the connections so skipping a
   *  meeting on the laptop does not ask again on the phone. Ids come from the
   *  feed (UID + occurrence start), so every device derives the same one. */
  dismissedEvents: string[];
}

export interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  durationMinutes: number;
  /** Local calendar day, YYYY-MM-DD. */
  date: string;
  calendarLabel: string;
  /** Best guess from the title; "" when nothing matched. */
  projectId: string;
}

export const EMPTY_CALENDAR_SETTINGS: CalendarSettings = {
  connections: [],
  minEventMinutes: DEFAULT_MIN_EVENT_MINUTES,
  dismissedEvents: [],
};

// ---- Settings store ---------------------------------------------------------

const CAL_KEY = "ph.calendars";
const listeners = new Set<(v: CalendarSettings) => void>();

function normalize(raw: unknown): CalendarSettings | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<CalendarSettings>;
  const connections = Array.isArray(r.connections)
    ? r.connections
        .filter((c): c is CalendarConnection => !!c && typeof c === "object")
        .map((c) => ({
          label: String(c.label ?? ""), icalUrl: String(c.icalUrl ?? ""), enabled: c.enabled !== false,
          suggest: c.suggest !== false,
          ...(typeof c.color === "string" && c.color ? { color: c.color } : {}),
        }))
    : [];
  const mins = Number(r.minEventMinutes);
  return {
    connections,
    minEventMinutes: Number.isFinite(mins) && mins >= 0 ? mins : DEFAULT_MIN_EVENT_MINUTES,
    dismissedEvents: Array.isArray(r.dismissedEvents) ? r.dismissedEvents.map(String) : [],
  };
}

export function loadCalendarSettings(): CalendarSettings {
  try {
    return normalize(JSON.parse(localStorage.getItem(CAL_KEY) || "null")) ?? EMPTY_CALENDAR_SETTINGS;
  } catch { return EMPTY_CALENDAR_SETTINGS; }
}

function writeLocal(next: CalendarSettings): void {
  try { localStorage.setItem(CAL_KEY, JSON.stringify(next)); } catch { /* private mode — server copy stands */ }
  for (const fn of listeners) fn(next);
}

/** null = not attempted yet; false = the server dropped the field. */
let persistedOnServer: boolean | null = null;
const persistListeners = new Set<(v: boolean | null) => void>();
function setPersisted(v: boolean): void {
  persistedOnServer = v;
  for (const fn of persistListeners) fn(v);
}

async function writeServer(next: CalendarSettings): Promise<void> {
  const userId = (pb?.authStore?.model as { id?: string } | null)?.id;
  if (appMode === "demo" || !userId) return;
  try {
    const saved = await pb.collection("users").update(userId, { calendar: next });
    // A collection without the field returns the record without it.
    setPersisted((saved as Record<string, unknown>).calendar != null);
  } catch {
    /* offline or refused — the local copy already applied */
  }
}

export function saveCalendarSettings(next: CalendarSettings): void {
  writeLocal(next);
  void writeServer(next);
}

/**
 * Adopt the calendars stored on the signed-in account (called from
 * AuthContext on every auth change, like the theme). The account copy wins when
 * it holds anything; when it holds nothing but this browser does — calendars
 * set up before the field existed — this browser's copy is lifted up once.
 */
let lifted = false;
export function adoptAccountCalendars(record: Record<string, unknown> | null): void {
  if (!record) return;
  const server = normalize(record.calendar);
  if (server && (server.connections.length || server.dismissedEvents.length)) {
    if (JSON.stringify(server) !== JSON.stringify(loadCalendarSettings())) writeLocal(server);
    persistedOnServer = true;
    return;
  }
  const local = loadCalendarSettings();
  if (!lifted && local.connections.some((c) => c.icalUrl.trim())) {
    lifted = true;
    void writeServer(local);
  }
}

export function useCalendarSettings(): {
  settings: CalendarSettings;
  save: (next: CalendarSettings) => void;
  persistedOnServer: boolean | null;
} {
  const [settings, setSettings] = useState<CalendarSettings>(loadCalendarSettings);
  const [persisted, setPersistedState] = useState<boolean | null>(persistedOnServer);
  useEffect(() => {
    listeners.add(setSettings);
    persistListeners.add(setPersistedState);
    return () => { listeners.delete(setSettings); persistListeners.delete(setPersistedState); };
  }, []);
  return { settings, save: saveCalendarSettings, persistedOnServer: persisted };
}

export const activeConnections = (s: CalendarSettings) =>
  s.connections.filter((c) => c.enabled && c.icalUrl.trim());

/** The calendars the Time card reads: active and not opted out of suggestions. */
export const suggestingConnections = (s: CalendarSettings) =>
  activeConnections(s).filter((c) => c.suggest !== false);

// ---- Fetch ------------------------------------------------------------------

interface WorkerEventDto {
  uid: string;
  summary: string;
  description?: string;
  start: string;
  end: string;
  calendarLabel: string;
}

export interface CalendarFetch {
  events: CalendarEvent[];
  errors: { label: string; message: string }[];
  fetchedAt: string;
}

/**
 * Best-effort project match on the event title: the longest project name found
 * in it, then a client name. Longest first, so "Acme Website Refresh" beats
 * "Acme". A wrong guess costs one dropdown change; no guess costs the same.
 */
export function guessProjectId(summary: string, projects: Project[], description?: string): string {
  // A work block says which project it is for (the Apps Script writes this
  // line into the event), so no guessing is needed — when that project still exists.
  const tagged = /Project Hub project: ([a-z0-9]{15})/.exec(description ?? "")?.[1];
  if (tagged && projects.some((p) => p.id === tagged)) return tagged;
  const text = summary.toLowerCase();
  const live = projects
    .filter((p) => p.status !== "archived" && p.kind !== "inbox" && p.name?.trim())
    .sort((a, b) => b.name.length - a.name.length);
  const byName = live.find((p) => text.includes(p.name.trim().toLowerCase()));
  if (byName) return byName.id;
  const byClient = live.find((p) => p.client?.trim() && text.includes(p.client.trim().toLowerCase()));
  return byClient?.id ?? "";
}

async function fetchCalendarEvents(settings: CalendarSettings, projects: Project[]): Promise<CalendarFetch> {
  const token = pb?.authStore?.token;
  if (!token) throw new Error("Sign in first.");
  const selfEmail = (pb.authStore.model as { email?: string } | null)?.email;

  const res = await fetch("/api/ical/events", {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: token },
    // Addresses go in the body, never the URL: query strings end up in request
    // logs, and each of these reads a whole calendar.
    body: JSON.stringify({
      calendars: suggestingConnections(settings).map((c) => ({ label: c.label, icalUrl: c.icalUrl.trim() })),
      days: CALENDAR_LOOKBACK_DAYS,
      selfEmail,
    }),
  });
  const body = await res.json().catch(() => ({})) as {
    events?: WorkerEventDto[]; errors?: CalendarFetch["errors"]; fetchedAt?: string; error?: string;
  };
  if (!res.ok) throw new Error(body.error ?? `Calendar sync failed (${res.status})`);

  // The Worker returns UTC instants; local days are worked out here, where the
  // browser's timezone actually is.
  const windowStart = new Date();
  windowStart.setDate(windowStart.getDate() - (CALENDAR_LOOKBACK_DAYS - 1));
  const startKey = toIsoLocal(windowStart);
  const todayKey = toIsoLocal(new Date());

  const events: CalendarEvent[] = [];
  for (const dto of body.events ?? []) {
    const start = new Date(dto.start);
    const end = new Date(dto.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const date = toIsoLocal(start);
    if (date < startKey || date > todayKey) continue;
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
    if (durationMinutes <= 0 || durationMinutes < settings.minEventMinutes) continue;
    events.push({
      id: `cal-${dto.uid}`,
      summary: dto.summary,
      start: dto.start,
      end: dto.end,
      durationMinutes,
      date,
      calendarLabel: dto.calendarLabel,
      projectId: guessProjectId(dto.summary, projects, dto.description),
    });
  }
  return { events, errors: body.errors ?? [], fetchedAt: body.fetchedAt ?? new Date().toISOString() };
}

/** Raw fetch state, shared by the Time card and Settings (same query key). */
export function useCalendarEvents(projects: Project[]) {
  const { settings } = useCalendarSettings();
  const active = suggestingConnections(settings);
  const connected = appMode !== "demo" && active.length > 0;
  const query = useQuery({
    // Keyed on the addresses and the length filter only — a dismissal must not
    // trigger a refetch. Projects are left out of the key: the guess is cheap
    // to be slightly stale, a refetch per project edit is not.
    queryKey: ["calendar-events", active.map((c) => [c.label, c.icalUrl.trim()]), settings.minEventMinutes],
    queryFn: () => fetchCalendarEvents(settings, projects),
    enabled: connected,
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return { settings, connected, query };
}

// ---- What the card shows ----------------------------------------------------

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Whether this event looks like time already on the books — same day and same
 * description, or overlapping clock times. Without it the card keeps offering
 * back everything you just logged from it.
 */
function isAlreadyLogged(event: CalendarEvent, entries: TimeEntry[]): boolean {
  const summary = norm(event.summary);
  const evStart = new Date(event.start).getTime();
  const evEnd = new Date(event.end).getTime();
  return entries.some((entry) => {
    if (!entry.start || toIsoLocal(new Date(entry.start)) !== event.date) return false;
    if (norm(entry.note || "") === summary) return true;
    const s = new Date(entry.start).getTime();
    const e = new Date(entry.end || entry.start).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return false;
    return s < evEnd && e > evStart;
  });
}

function dismissEvents(ids: string[]) {
  const current = loadCalendarSettings();
  const next = [...new Set([...current.dismissedEvents, ...ids])];
  if (next.length !== current.dismissedEvents.length) saveCalendarSettings({ ...current, dismissedEvents: next });
}

/**
 * Everything the "From your calendar" card needs. Recomputed as entries change,
 * so an event drops off the moment it is covered rather than at the next fetch.
 */
export function useCalendarSuggestions(entries: TimeEntry[], projects: Project[]) {
  const { settings, connected, query } = useCalendarEvents(projects);
  const events = useMemo(() => query.data?.events ?? [], [query.data]);

  // Forget dismissals for occurrences the feed no longer returns, so the list
  // cannot grow without bound as the window rolls forward. Only writes when
  // something actually aged out, and only after a successful fetch.
  useEffect(() => {
    if (!query.data || query.data.errors.length) return;
    const live = new Set(query.data.events.map((e) => e.id));
    const kept = settings.dismissedEvents.filter((id) => live.has(id));
    if (kept.length !== settings.dismissedEvents.length) {
      saveCalendarSettings({ ...loadCalendarSettings(), dismissedEvents: kept });
    }
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => {
    const dismissed = new Set(settings.dismissedEvents);
    return events
      .filter((e) => !dismissed.has(e.id) && !isAlreadyLogged(e, entries))
      .sort((a, b) => b.start.localeCompare(a.start))
      .slice(0, MAX_CALENDAR_SUGGESTIONS);
  }, [events, entries, settings.dismissedEvents]);

  return {
    connected,
    suggestions,
    errors: query.data?.errors ?? [],
    failure: query.error instanceof Error ? query.error.message : null,
    loading: query.isFetching,
    dismiss: (id: string) => dismissEvents([id]),
    dismissAll: () => dismissEvents(suggestions.map((s) => s.id)),
  };
}

/**
 * One day's meetings that are over with nothing logged against them: the
 * Today page's "Not logged" list. The card's rules (the calendars with Suggest
 * in Time on, the length filter, dismissals, `isAlreadyLogged`), but every one
 * on that day rather than the newest few across the whole window. Dismissing
 * here dismisses on the card too.
 */
export function useUnloggedMeetings(entries: TimeEntry[], projects: Project[], day: string, now: Date) {
  const { settings, connected, query } = useCalendarEvents(projects);
  const meetings = useMemo(() => {
    const dismissed = new Set(settings.dismissedEvents);
    return (query.data?.events ?? [])
      .filter((e) => e.date === day && new Date(e.end) <= now && !dismissed.has(e.id) && !isAlreadyLogged(e, entries))
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [query.data, entries, settings.dismissedEvents, day, now]);
  return { connected, meetings, dismiss: (id: string) => dismissEvents([id]) };
}

// ---- The "not connected" hint -----------------------------------------------

const HINT_KEY = "ph.calendarHintDismissed";
export function useCalendarHint(): [boolean, () => void] {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HINT_KEY) === "1"; } catch { return false; }
  });
  return [hidden, () => {
    setHidden(true);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* private mode */ }
  }];
}
